import React, { useEffect, useState } from 'react';
import { X, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import Button from '../ui/Button';
import { Input } from '../ui/Input';
import { api } from '../../services/api';

// Renderiza um campo dinâmico do formulário de inscrição.
function DynamicField({ field, value, onChange }) {
  const required = field.required ? <span className="text-rose-500"> *</span> : null;
  const label = (
    <label className="mb-1 block text-sm font-semibold text-ink">
      {field.label}
      {required}
    </label>
  );

  switch (field.type) {
    case 'select':
      return (
        <div>
          {label}
          <select
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            className="w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-ink outline-none transition-colors focus:border-primary focus:ring-2 focus:ring-primary/20"
          >
            <option value="">Selecione...</option>
            {(field.options || []).map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>
        </div>
      );
    case 'checkbox':
      return (
        <div>
          {label}
          <div className="space-y-2">
            {(field.options || []).map((opt) => (
              <label key={opt} className="flex items-center gap-2.5 text-sm text-ink">
                <input
                  type="checkbox"
                  checked={(value || []).includes(opt)}
                  onChange={(e) => {
                    const next = e.target.checked
                      ? [...(value || []), opt]
                      : (value || []).filter((v) => v !== opt);
                    onChange(next);
                  }}
                  className="h-4 w-4 rounded border-slate-300 text-primary focus:ring-primary/30"
                />
                {opt}
              </label>
            ))}
          </div>
        </div>
      );
    case 'number':
      return (
        <div>
          {label}
          <Input
            type="number"
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder={field.placeholder || ''}
          />
        </div>
      );
    case 'email':
      return (
        <div>
          {label}
          <Input
            type="email"
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder={field.placeholder || 'voce@email.com'}
          />
        </div>
      );
    case 'phone':
      return (
        <div>
          {label}
          <Input
            type="tel"
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder={field.placeholder || '(33) 99999-9999'}
          />
        </div>
      );
    default:
      return (
        <div>
          {label}
          <Input
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder={field.placeholder || ''}
          />
        </div>
      );
  }
}

export default function RegistrationDialog({ event, onClose, onSuccess }) {
  const [form, setForm] = useState({ name: '', phone: '', email: '' });
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  if (!event) return null;

  const fields = Array.isArray(event.formFields) ? event.formFields : [];

  function setAnswer(key, value) {
    setAnswers((prev) => ({ ...prev, [key]: value }));
  }

  async function submit(e) {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error('Preencha nome e telefone.');
      return;
    }
    // Valida campos dinâmicos obrigatórios no cliente
    for (const field of fields) {
      if (field.required) {
        const v = answers[field.key];
        const empty = v === undefined || v === null || v === '' || (Array.isArray(v) && v.length === 0);
        if (empty) {
          toast.error(`O campo "${field.label}" é obrigatório.`);
          return;
        }
      }
    }
    setLoading(true);
    try {
      await api.registerForEvent(event.id, { ...form, answers });
      toast.success('Inscrição confirmada! Nos vemos lá.');
      onSuccess?.();
      onClose();
    } catch (err) {
      toast.error(err.message || 'Não foi possível concluir a inscrição.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center md:items-center md:p-6" data-testid="registration-dialog">
      <div
        className="absolute inset-0 bg-ink/40 backdrop-blur-sm"
        onClick={onClose}
        data-testid="registration-overlay"
      />
      <div className="relative max-h-[88vh] w-full max-w-[480px] animate-sheet-up overflow-y-auto rounded-t-2xl bg-white p-5 pb-8 shadow-lift md:max-w-lg md:rounded-2xl md:animate-fade-up">
        <div className="mx-auto mb-4 h-1.5 w-10 rounded-full bg-slate-200 md:hidden" />
        <button
          onClick={onClose}
          className="absolute right-4 top-4 flex h-9 w-9 items-center justify-center rounded-full bg-slate-100 text-ink-soft hover:bg-slate-200"
          data-testid="registration-close"
          aria-label="Fechar"
        >
          <X size={18} />
        </button>

        <h3 className="pr-10 font-heading text-xl font-extrabold text-ink">Inscrição</h3>
        <p className="mt-0.5 text-sm text-ink-soft">{event.title}</p>

        <form onSubmit={submit} className="mt-5 space-y-3.5">
          <Input
            label="Nome completo"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="Seu nome"
            data-testid="registration-name"
          />
          <Input
            label="WhatsApp / Telefone"
            value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
            placeholder="(33) 99999-9999"
            data-testid="registration-phone"
          />
          <Input
            label="E-mail (opcional)"
            type="email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            placeholder="voce@email.com"
            data-testid="registration-email"
          />

          {fields.map((field) => (
            <DynamicField
              key={field.key}
              field={field}
              value={answers[field.key]}
              onChange={(v) => setAnswer(field.key, v)}
            />
          ))}

          <Button type="submit" className="mt-2 w-full" disabled={loading} data-testid="registration-submit">
            {loading ? <Loader2 size={18} className="animate-spin" /> : 'Confirmar inscrição'}
          </Button>
        </form>
      </div>
    </div>
  );
}
